import type { ChangeEvent } from "react";
import { useState } from "react";
import styles from "./CustomHeader.module.scss";
import Button from "@/components/Button/Button";
import { Select } from "@/components/FormControl/Select/Select";
import SearchIcon from "@/components/SVGComponents/SearchIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import { combineClasses } from "@/lib/utils";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import type { CustomHeaderProps } from "@/types/common";
import CrossIcon from "@/components/SVGComponents/CrossIcon";

const ALL_OPTION = { label: "All", value: "" };

const CustomHeader = ({
  titleName,
  search,
  setSearch,
  searchPlaceholder = "Search",
  tabs,
  activeTab,
  onTabChange,
  onExport,
  exportLabel = "EXPORT CSV",
  onAddClick,
  addButtonTitle,
  formConfig,
  onFilterSubmit,
  showSearch = true,
  hideExport = false,
  tabsPosition = "inline",
}: CustomHeaderProps) => {
  const [filterState, setFilterState] = useState<Record<string, string>>({});

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch?.(e.target.value);
  };

  const handleFilterChange = (name: string, value: string) => {
    const next = { ...filterState, [name]: value };
    setFilterState(next);
    if (onFilterSubmit) {
      onFilterSubmit(Object.fromEntries(Object.entries(next).map(([key, val]) => [key, val || undefined])));
    }
  };

  const renderActionContent = (icon: React.ReactNode, label: string) => (
    <span className={styles.buttonContent}>
      {icon}
      <span className={styles.buttonLabel}>{label}</span>
    </span>
  );

  return (
    <div className={styles.headerWrapper}>
      <div className={styles.leftSection}>
        {titleName && <h2 className={styles.title}>{titleName}</h2>}
        {tabs && tabsPosition === "below" && (
          <div className={styles.tabsBelowWrapper}>
            {tabs.map((tab) => (
              <div
                key={tab.value}
                className={combineClasses(styles.tabsBelowButton, activeTab === tab.value && styles.tabsBelowButtonActive)}
                onClick={() => onTabChange?.(tab.value)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {showSearch ? (
        <div className={styles.searchBox}>
          <SearchIcon className={styles.searchIcon} />
          <input value={search ?? ""} onChange={onSearchChange} type="text" placeholder={searchPlaceholder} />
          {search && (
            <CrossIcon
              onClick={() => setSearch?.("")}
              withBg={false}
              height={12}
              width={12}
              strokeWidth={2}
              className={styles.crossIcon}
            />
          )}
        </div>
      ) : null}

      <div className={styles.rightSection}>
        {tabs && tabsPosition === "inline" && (
          <div className={styles.filterTabsWrapper}>
            {tabs.map((tab) => (
              <div
                key={tab.value}
                className={combineClasses(styles.filterTabButton, activeTab === tab.value && styles.activeFilterTabButton)}
                onClick={() => onTabChange?.(tab.value)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        )}
        {formConfig &&
          Object.entries(formConfig).map(([name, field]) => {
            if (field.hide) return null;

            const options = (field.props?.options as { label: string; value: string }[] | undefined) || [];
            const allOptions = [ALL_OPTION, ...options];
            const selected = allOptions.find((option) => option.value === (filterState[name] ?? "")) ?? ALL_OPTION;

            return (
              <Select
                key={name}
                name={name}
                value={selected}
                options={allOptions}
                isSearchable={false}
                className={combineClasses(styles.filterSelect)}
                onChange={(option) => handleFilterChange(name, (option as { value: string } | null)?.value ?? "")}
                formatOptionLabel={(option, meta) =>
                  meta.context === "value" ? `${field.label}: ${option.label}` : option.label
                }
              />
            );
          })}

        {!hideExport ? (
          <Button onClick={onExport || undefined} className={combineClasses(styles.actionButton, styles.exportButton)} outlined>
            {renderActionContent(<DownloadIcon className={combineClasses(styles.buttonIcon, styles.exportSVG)} />, exportLabel)}
          </Button>
        ) : null}

        {addButtonTitle && onAddClick ? (
          <Button onClick={onAddClick} className={combineClasses(styles.actionButton, styles.addButton)}>
            {renderActionContent(<PlusIcon className={combineClasses(styles.buttonIcon, styles.addIcon)} />, addButtonTitle)}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default CustomHeader;
